import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from "react";
import { api } from "@/lib/api";
import { getActionableError } from "@/lib/errors";

const ALLOWED_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);
const MAX_FILES = 3;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_TOTAL_BYTES = 12 * 1024 * 1024;

const ContextScreenshotInput = forwardRef(function ContextScreenshotInput({
  purpose, resourceId, guidance = "", remainingCharacters, onStateChange,
}, ref) {
  const inputId = useId();
  const filesRef = useRef([]);
  const busyRef = useRef(false);
  const [files, setFiles] = useState([]);
  const [items, setItems] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    onStateChange?.(analyzing ? "analyzing" : items !== null ? "review" : files.length ? "selected" : "empty");
  }, [analyzing, files.length, items, onStateChange]);

  useEffect(() => () => {
    filesRef.current.forEach(({ url }) => URL.revokeObjectURL(url));
    onStateChange?.("empty");
  }, [onStateChange]);

  const addFiles = (incoming) => {
    const selected = Array.from(incoming || []);
    if (!selected.length || busyRef.current) return false;
    const existing = filesRef.current;
    if (existing.length + selected.length > MAX_FILES) {
      setError("Add up to 3 images.");
      return false;
    }
    if (selected.some((file) => !ALLOWED_TYPES.has(file.type) || file.size > MAX_FILE_BYTES)) {
      setError("Use PNG, JPEG, or WebP images under 5 MB each.");
      return false;
    }
    if ([...existing.map(({ file }) => file), ...selected].reduce((total, file) => total + file.size, 0) > MAX_TOTAL_BYTES) {
      setError("The images must be under 12 MB together.");
      return false;
    }
    const next = [...existing, ...selected.map((file) => ({
      id: crypto.randomUUID(), file, url: URL.createObjectURL(file),
    }))];
    filesRef.current = next;
    setFiles(next);
    setItems(null);
    setConflicts([]);
    setError("");
    return true;
  };

  const analyze = async () => {
    if (!files.length || !resourceId || busyRef.current) return;
    busyRef.current = true;
    const form = new FormData();
    form.append("purpose", purpose);
    form.append("resource_id", resourceId);
    form.append("instructions", guidance.slice(0, 1000));
    files.forEach(({ file }) => form.append("images", file));
    setAnalyzing(true);
    setError("");
    try {
      const { data } = await api.post("/private-context/analyze-screenshots", form);
      const extracted = Array.isArray(data?.items)
        ? data.items
        : data?.context ? [{ text: data.context, kind: "uncertain" }] : [];
      setItems(extracted.map((item, index) => ({
        id: `item-${index}`, text: String(item.text || ""), kind: item.kind,
        included: item.kind !== "uncertain",
      })));
      setConflicts((Array.isArray(data?.conflicts) ? data.conflicts : []).map((text, index) => ({
        id: `conflict-${index}`, text: String(text), included: false,
      })));
    } catch (err) {
      setError(getActionableError(err, "Callsect could not read these images."));
    } finally {
      busyRef.current = false;
      setAnalyzing(false);
    }
  };

  useImperativeHandle(ref, () => ({
    addFiles,
    async prepareSubmission() {
      if (!files.length) return { ready: true, context: "" };
      if (busyRef.current) return { ready: false };
      if (remainingCharacters <= 0) {
        setError("Shorten your note to make room for image context before reading the images.");
        return { ready: false };
      }
      if (items === null) {
        await analyze();
        return { ready: false };
      }
      const approved = [
        ...items.filter((item) => item.included && item.text.trim()).map((item) =>
          `${item.kind === "internal" ? "Internal note: " : item.kind === "uncertain" ? "Uncertain; verify: " : ""}${item.text.trim()}`),
        ...conflicts.filter((item) => item.included && item.text.trim()).map((item) =>
          `Conflicting information; verify: ${item.text.trim()}`),
      ];
      if (!approved.length) {
        setError("Select at least one point to use, or remove the images.");
        return { ready: false };
      }
      const context = `Image context reviewed by the user:\n${approved.map((point) => `- ${point}`).join("\n")}`;
      if (context.length > remainingCharacters) {
        setError(`Shorten the selected points or your note to fit within ${Math.max(0, remainingCharacters)} characters.`);
        return { ready: false };
      }
      setError("");
      return { ready: true, context };
    },
  }));

  const removeFile = (id) => {
    const entry = filesRef.current.find((item) => item.id === id);
    if (entry) URL.revokeObjectURL(entry.url);
    const next = filesRef.current.filter((item) => item.id !== id);
    filesRef.current = next;
    setFiles(next);
    setItems(null);
    setConflicts([]);
    setError("");
  };

  const updatePoint = (setter, id, field, value) => {
    setter((current) => current.map((item) => item.id === id ? { ...item, [field]: value } : item));
    setError("");
  };

  const renderPoint = (point, setter, label) => (
    <div className="context-screenshot-input__point" key={point.id}>
      <label className="context-screenshot-input__point-head">
        <input type="checkbox" checked={point.included}
          onChange={(event) => updatePoint(setter, point.id, "included", event.target.checked)} />
        <span>{label}</span>
      </label>
      <textarea aria-label={`Edit ${label.toLowerCase()}`} value={point.text} maxLength={400} rows={2}
        onChange={(event) => updatePoint(setter, point.id, "text", event.target.value)} />
      <button type="button" onClick={() => setter((current) => current.filter((item) => item.id !== point.id))}>Remove point</button>
    </div>
  );

  return (
    <div className="context-screenshot-input">
      <div className="context-screenshot-input__head">
        <label htmlFor={inputId}>Add images</label>
        <span>Up to 3 · PNG, JPG or WebP</span>
      </div>
      <div className="context-screenshot-input__drop" onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          addFiles(event.dataTransfer.files);
        }}>
        <span>Paste a screenshot in the note with Ctrl+V, or drop images here.</span>
        <label htmlFor={inputId}>Choose images</label>
        <input id={inputId} type="file" accept="image/png,image/jpeg,image/webp" multiple
          onChange={(event) => {
            addFiles(event.target.files);
            event.target.value = "";
          }} disabled={analyzing} />
      </div>
      {files.length ? (
        <ul className="context-screenshot-input__files">
          {files.map(({ id, file, url }) => (
            <li key={id}>
              <img src={url} alt="Attached image preview" />
              <span title={file.name}>{file.name}</span>
              <button type="button" aria-label={`Remove ${file.name}`} disabled={analyzing}
                onClick={() => removeFile(id)}>Remove</button>
            </li>
          ))}
        </ul>
      ) : null}
      {items !== null ? (
        <div className="context-screenshot-input__review">
          <strong>Review image details before continuing</strong>
          <p>Keep, edit, or remove each point. Uncertain and conflicting details are excluded until you select them.</p>
          {items.map((item) => renderPoint(item, setItems,
            item.kind === "confirmed" ? "Confirmed in source" : item.kind === "internal" ? "Internal note" : "Uncertain — verify"))}
          {conflicts.map((item) => renderPoint(item, setConflicts, "Conflicting information — verify"))}
          <small>Only the points you approve become private context. The images are not saved by Callsect.</small>
        </div>
      ) : files.length ? (
        <p className="context-screenshot-input__hint">
          The next click reads all images together using AI tokens; it does not use an email or preparation generation. You will review the results before generation.
        </p>
      ) : null}
      {error ? <p className="context-screenshot-input__error" role="alert">{error}</p> : null}
    </div>
  );
});

export default ContextScreenshotInput;
